import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { useLocation, useNavigate, Location, NavigateFunction } from "react-router-dom";
import { getClients, IClient, updateClient, deleteClient } from "../../../redux/modules/clients";
import { RootState } from "../../../redux";
import TypeaheadSearch, { SearchType } from "../../TypeaheadSearch";
import "./client-list.css";

interface ClientListProps {
  clients?: IClient[];
  count?: number;
  updateClient?: (client: IClient) => void;
  deleteClient?: (clientId: number) => void;
  getClients?: (page: number, includeArchived?: boolean) => Promise<void>;
  location?: Location;
  navigate?: NavigateFunction;
}

type ClientListState = {
  page: number;
};

class ClientListPage extends React.Component<ClientListProps, ClientListState> {
  constructor(props: ClientListProps) {
    super(props);
    this.state = { page: this.getPageFromUrl() };
  }

  getPageFromUrl = (): number => {
    const params = new URLSearchParams(this.props.location?.search || "");
    const p = parseInt(params.get("page") || "1", 10);
    return Math.max(0, isNaN(p) ? 0 : p - 1);
  };

  getArchivedFromUrl = (): boolean => {
    const params = new URLSearchParams(this.props.location?.search || "");
    return params.get("archived") === "true";
  };

  buildUrl = (page: number, archived: boolean): string => {
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    if (archived) params.set("archived", "true");
    return `?${params.toString()}`;
  };

  componentDidMount() {
    const page = this.getPageFromUrl();
    const archived = this.getArchivedFromUrl();
    this.props.getClients && this.props.getClients(page, archived);
  }

  componentDidUpdate(prevProps: ClientListProps) {
    if (prevProps.location?.search !== this.props.location?.search) {
      const page = this.getPageFromUrl();
      const archived = this.getArchivedFromUrl();
      this.setState({ page });
      this.props.getClients && this.props.getClients(page, archived);
    }
  }

  renderClients() {
    if (!this.props.clients) {
      return (
        <div className="client-list__empty">
          <p className="client-list__loading">Триває пошук...</p>
        </div>
      );
    }

    const sorted = [...this.props.clients].sort((c1, c2) => c1.name.localeCompare(c2.name));

    if (sorted.length === 0) {
      return (
        <div className="client-list__empty">
          <p className="client-list__empty-text">Клієнти не знайдені</p>
        </div>
      );
    }

    return sorted.map((client) => (
      <div key={client.id} className={`client-list__card ${client.archived ? "client-list__card--archived" : ""}`}>
        <div className="client-list__card-content">
          <Link to={`/client/${client.id}`} className="client-list__card-link">
            <div className="client-list__card-icon">👤</div>
            <div className="client-list__card-info">
              <h3 className="client-list__card-name">{client.name}</h3>
              {client.phone && (
                <p className="client-list__card-detail">
                  <span className="client-list__card-label">Телефон:</span> {client.phone}
                </p>
              )}
              {client.email && (
                <p className="client-list__card-detail">
                  <span className="client-list__card-label">Email:</span> {client.email}
                </p>
              )}
            </div>
          </Link>
          <div className="client-list__card-actions">{this.renderButtons(client)}</div>
        </div>
      </div>
    ));
  }

  renderButtons(client: IClient) {
    if (!client.archived) {
      return (
        <button type="button" className="client-list__btn-archive" onClick={() => this.archiveClient(client)} title="Архівувати клієнта">
          <span className="client-list__btn-icon">🗄️</span>
        </button>
      );
    }
    return <span className="client-list__badge-archived">Архівований</span>;
  }

  render() {
    const showArchived = this.getArchivedFromUrl();

    return (
      <div className="client-list">
        <div className="client-list__header">
          <h1 className="client-list__title">Клієнти</h1>
          <p className="client-list__subtitle">Управління базою клієнтів</p>
        </div>

        <div className="client-list__actions">
          <Link to="/new-client" className="client-list__btn-add">
            <span className="client-list__btn-add-icon">➕</span>
            Додати Клієнта
          </Link>
        </div>

        <div className="client-list__search-section">
          <div className="client-list__search-card">
            <h2 className="client-list__search-title">Швидкий пошук</h2>
            <TypeaheadSearch searchType={SearchType.CLIENTS} />
          </div>
        </div>

        <div className="client-list__filters">
          <div className="client-list__filter-toggle">
            <button
              className={`client-list__filter-btn${!showArchived ? " client-list__filter-btn--active" : ""}`}
              onClick={() => this.setArchived(false)}
            >
              Тільки активні
            </button>
            <button
              className={`client-list__filter-btn${showArchived ? " client-list__filter-btn--active" : ""}`}
              onClick={() => this.setArchived(true)}
            >
              Тільки архівні
            </button>
          </div>
        </div>

        <div className="client-list__content">
          <div className="client-list__grid">{this.renderClients()}</div>
          {this.renderPagination()}
        </div>
      </div>
    );
  }

  renderPagination = () => {
    const { count } = this.props;
    if (!count) return null;

    const PAGE_SIZE = 20;
    const totalPages = Math.ceil(count / PAGE_SIZE);
    if (totalPages <= 1) return null;

    const current = this.state.page;
    const archived = this.getArchivedFromUrl();
    const pages: (number | "ellipsis")[] = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      const visible = new Set(
        [0, totalPages - 1, current - 2, current - 1, current, current + 1, current + 2].filter((p) => p >= 0 && p < totalPages)
      );
      const sorted = Array.from(visible).sort((a, b) => a - b);
      for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push("ellipsis");
        pages.push(sorted[i]);
      }
    }

    return (
      <div className="client-list__pagination">
        <button
          className="client-list__pagination-btn client-list__pagination-btn--nav"
          disabled={current === 0}
          onClick={() => this.goToPage(current - 1, archived)}
        >
          ←
        </button>
        {pages.map((p, i) =>
          p === "ellipsis" ? (
            <span key={`ellipsis-${i}`} className="client-list__pagination-ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`client-list__pagination-btn${p === current ? " client-list__pagination-btn--active" : ""}`}
              onClick={() => this.goToPage(p, archived)}
            >
              {p + 1}
            </button>
          )
        )}
        <button
          className="client-list__pagination-btn client-list__pagination-btn--nav"
          disabled={current === totalPages - 1}
          onClick={() => this.goToPage(current + 1, archived)}
        >
          →
        </button>
      </div>
    );
  };

  goToPage = (page: number, archived: boolean) => {
    this.props.navigate && this.props.navigate(this.buildUrl(page, archived));
  };

  setArchived = (archived: boolean) => {
    this.props.navigate && this.props.navigate(this.buildUrl(0, archived));
  };

  archiveClient = (client: IClient) => {
    if (window.confirm(`Ви впевнені, що хочете архівувати клієнта "${client.name}"?`)) {
      this.props.deleteClient && this.props.deleteClient(client.id);
    }
  };
}

const mapStateToProps = (state: RootState) => {
  return { clients: Object.values(state.clientsState.clients), count: state.clientsState.count };
};

const ConnectedClientListPage = connect(mapStateToProps, { getClients, updateClient, deleteClient })(ClientListPage);

function ClientListPageWithRouter(props: object) {
  const location = useLocation();
  const navigate = useNavigate();
  return <ConnectedClientListPage {...props} location={location} navigate={navigate} />;
}

export default ClientListPageWithRouter;
